import { envSchema } from './schema'

/**
 * Thrown at load time when the environment fails schema validation. Carries the
 * list of offending variable names in `message` and the raw violations on `cause`
 * for structured inspection.
 */
export class EnvValidationError extends Error {
  constructor(message, options) {
    super(message, options)
    this.name = 'EnvValidationError'
  }
}

/**
 * Validates the raw Vite env against the config schema at load time. Throws
 * immediately with a list of every missing/empty required variable so that
 * misconfiguration fails fast at startup (during app init) rather than
 * surfacing later as an undefined value deep in some unrelated component.
 * @param {Record<string, string>} rawEnv - The raw `import.meta.env` object.
 * @returns {Record<string, string>} Only the keys declared in `envSchema` — undeclared
 * keys (e.g. `MODE`, `DEV`, `BASE_URL`) are stripped.
 */
export const validateEnv = rawEnv => {
  const violations = []
  const result = {}

  for (const [key, { required }] of Object.entries(envSchema)) {
    const value = rawEnv[key]

    if (required && (value === undefined || value === '')) {
      violations.push(key)
    }

    result[key] = value
  }

  if (violations.length > 0) {
    throw new EnvValidationError(
      `Invalid environment configuration (src/config). Fix the following variable(s): ${violations.join(', ')}.\n` +
        'See config/secrets.enc, .env.local, and src/config/README.md for how to set them.',
      { cause: violations },
    )
  }

  return result
}
